import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useAcceptAffiliateInvitation, useAffiliateData } from '@/hooks/useAffiliates';

export const Route = createFileRoute('/affiliate/signup')({
  component: AffiliateSignup,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      inviteId: (search.inviteId as string) || undefined,
    };
  },
});

function AffiliateSignup() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { inviteId } = Route.useSearch();
  const { data: affiliateData, isLoading: isLoadingAffiliate } = useAffiliateData();
  const acceptInvitation = useAcceptAffiliateInvitation();

  const [invitationData, setInvitationData] = useState<any>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    paymentEmail: currentUser?.email || '',
  });

  // Fetch invitation data
  useEffect(() => {
    if (!inviteId) {
      setInvitationError('No invitation ID provided');
      setIsLoadingInvitation(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const { db } = await import('@/firebase/config');
        const { doc, getDoc } = await import('firebase/firestore');

        if (!db) {
          throw new Error('Firestore not available');
        }

        const invitationRef = doc(db, 'affiliateInvitations', inviteId);
        const invitationSnap = await getDoc(invitationRef);

        if (!invitationSnap.exists()) {
          setInvitationError('Invitation not found');
        } else {
          const data = invitationSnap.data();

          if (data.status !== 'pending') {
            setInvitationError('This invitation has already been used or expired');
          } else {
            setInvitationData(data);

            // Pre-fill display name from invitation if available
            if (data.displayName && !formData.displayName) {
              setFormData(prev => ({ ...prev, displayName: data.displayName }));
            }
          }
        }
      } catch (error: any) {
        setInvitationError(error.message || 'Failed to load invitation');
      } finally {
        setIsLoadingInvitation(false);
      }
    };

    fetchInvitation();
  }, [inviteId]);

  // Check if user already has affiliate account
  useEffect(() => {
    if (affiliateData && !isLoadingAffiliate) {
      // User already has an affiliate account, redirect to dashboard
      navigate({ to: '/affiliate/dashboard' });
    }
  }, [affiliateData, isLoadingAffiliate, navigate]);

  const handleAcceptInvitation = () => {
    if (!formData.displayName) return;

    acceptInvitation.mutate(
      {
        ...formData,
        inviteId: inviteId,
      },
      {
        onSuccess: () => {
          // Navigate to dashboard after successful signup
          navigate({ to: '/affiliate/dashboard' });
        },
      }
    );
  };

  // Handle not logged in
  if (!currentUser) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-6 w-6" />
                Join Affiliate Program
              </CardTitle>
              <CardDescription>
                Sign in to accept your affiliate invitation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Please sign in to continue with accepting this affiliate invitation.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate({ to: '/' })} className="flex-1">
                  Go to Home & Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Handle loading invitation
  if (isLoadingInvitation || isLoadingAffiliate) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Handle invitation error
  if (invitationError || !invitationData) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-red-500" />
                Invalid Invitation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {invitationError || 'This invitation is not valid'}
                </AlertDescription>
              </Alert>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate({ to: '/' })} variant="outline">
                  Go to Home
                </Button>
                <Button onClick={() => navigate({ to: '/affiliate/dashboard' })}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Show signup form
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              Join Affiliate Program
            </CardTitle>
            <CardDescription>
              You've been invited to join the Plot My Travel affiliate program
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                Complete your profile to start earning commissions by sharing Plot My Travel with your audience.
              </AlertDescription>
            </Alert>

            {invitationData.email && (
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Invitation sent to</p>
                <p className="font-medium">{invitationData.email}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  placeholder="Your name or brand"
                />
                <p className="text-xs text-muted-foreground">
                  This is how you'll appear in the affiliate dashboard
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentEmail">Payment Email *</Label>
                <Input
                  id="paymentEmail"
                  type="email"
                  value={formData.paymentEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, paymentEmail: e.target.value })
                  }
                  placeholder="Where you want to receive payments"
                />
                <p className="text-xs text-muted-foreground">
                  We'll use this email for payment notifications and payouts
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleAcceptInvitation}
                disabled={acceptInvitation.isPending || !formData.displayName || !formData.paymentEmail}
                className="flex-1"
              >
                {acceptInvitation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  'Accept & Join Program'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate({ to: '/' })}
                disabled={acceptInvitation.isPending}
              >
                Cancel
              </Button>
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                By joining the affiliate program, you agree to our terms of service and affiliate program policies.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
